import React from 'react';
import { phoneHref } from '../../lib/contactHref';

interface PhoneCallLinkProps extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'children'> {
  /** The phone text as stored. Never used as a link target directly: it goes through phoneHref. */
  readonly phone: string | null | undefined;
  /** What to render when the number cannot be read with certainty: nothing, or the children as plain text. */
  readonly whenNoLink?: 'hide' | 'text';
  /**
   * Shown instead of nothing when the number cannot be dialled and the children are only a label
   * ("Call Customer"): e.g. the raw number as text, so a technician still sees it. Wins over whenNoLink.
   */
  readonly fallback?: React.ReactNode;
  readonly children: React.ReactNode;
}

/** Props that only mean something on a link; dropped from the plain-text fallback. */
const LINK_ONLY_PROPS = new Set([
  'target', 'rel', 'download', 'hrefLang', 'ping', 'referrerPolicy', 'type', 'media', 'aria-label', 'title',
]);

/**
 * The one `tel:` link in the client (Support first, every screen since ISS-571; CI: check-no-raw-tel). A link is built only for numbers
 * phoneHref can read with certainty; anything else is hidden or shown as text, because a wrong
 * dial is worse than no link.
 */
export function PhoneCallLink({
  phone, whenNoLink = 'hide', fallback, className, children, ...anchorProps
}: PhoneCallLinkProps): React.ReactElement | null {
  const href = phoneHref('tel', phone);
  if (href) return <a {...anchorProps} href={href} className={className}>{children}</a>;
  if (fallback !== undefined) return <>{fallback}</>;
  if (whenNoLink === 'hide') return null;
  // Plain text keeps the handlers the link carried (a card's stopPropagation / no-drag guard), but
  // nothing that only makes sense on a link: no target/rel, and no "Call ..." label on text that
  // does not call.
  const spanProps = Object.fromEntries(
    Object.entries(anchorProps).filter(([key]) => !LINK_ONLY_PROPS.has(key)),
  ) as React.HTMLAttributes<HTMLSpanElement>;
  return <span {...spanProps} className={className}>{children}</span>;
}
